/**
 * "How you compare" — the user's product against every competitor and the
 * competitor median, one row each, with the same metric definitions Mira uses.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { METRICS, TIER_BAR, TIER_TEXT, median, standing, type MetricDef, type MetricRow } from "./metrics";

export interface BenchmarkEntity extends MetricRow {
  name: string;
  isSelf?: boolean;
}

interface Props {
  self: BenchmarkEntity | null;
  competitors: BenchmarkEntity[];
  isPaid: boolean;
  isLoading: boolean;
}

export function BenchmarkTable({ self, competitors, isPaid, isLoading }: Props) {
  const metrics = METRICS;
  const medians = Object.fromEntries(metrics.map((m) => [m.key, median(competitors.map(m.value))]));
  const rows = [...(self ? [self] : []), ...competitors];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full min-w-[860px] border-collapse text-left">
          <thead>
            <tr className="border-b border-border">
              <th className="sticky left-0 z-10 bg-card px-4 py-2.5 font-mono text-[11px] font-normal uppercase tracking-widest text-muted-foreground">
                Site
              </th>
              {metrics.map((m) => (
                <th key={m.key} className="px-3 py-2.5 text-right font-normal">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
                        {m.short}
                        <Icon name="information-circle" size={11} className="opacity-50" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">
                      <p className="font-medium">{m.label}</p>
                      <p className="mt-1 text-muted-foreground">{m.help}</p>
                    </TooltipContent>
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.domain}
                className={cn("border-b border-border/50 last:border-b-0", row.isSelf && "bg-foreground/[0.03]")}
              >
                <td className={cn("sticky left-0 z-10 bg-card px-4 py-3", row.isSelf && "border-l-2 border-l-foreground/60")}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Favicon domain={row.domain} name={row.name} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-sans text-xs font-medium">{row.name}</span>
                        {row.isSelf && (
                          <span className="rounded border border-border px-1.5 py-px font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                            You
                          </span>
                        )}
                      </div>
                      <p className="truncate font-mono text-[11px] text-muted-foreground">{row.domain}</p>
                    </div>
                  </div>
                </td>
                {metrics.map((m) => (
                  <MetricCell
                    key={m.key}
                    def={m}
                    row={row}
                    benchmark={row.isSelf ? medians[m.key] : null}
                    locked={!!m.paidOnly && !isPaid}
                    isLoading={isLoading}
                  />
                ))}
              </tr>
            ))}

            {competitors.length > 1 && (
              <tr className="border-t border-border bg-muted/20">
                <td className="sticky left-0 z-10 bg-muted/20 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Competitor median
                </td>
                {metrics.map((m) => {
                  const v = medians[m.key];
                  return (
                    <td key={m.key} className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
                      {m.paidOnly && !isPaid ? "" : v != null ? m.format(v) : "—"}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!isPaid && (
        <p className="mt-2 font-sans text-[11px] text-muted-foreground">
          Search traffic and keywords are on paid plans.{" "}
          <Link to="/settings?tab=billing" className="text-foreground underline-offset-2 hover:underline">Upgrade</Link>
        </p>
      )}
    </TooltipProvider>
  );
}

function MetricCell({
  def, row, benchmark, locked, isLoading,
}: { def: MetricDef; row: BenchmarkEntity; benchmark: number | null; locked: boolean; isLoading: boolean }) {
  if (locked) {
    return (
      <td className="px-3 py-3 text-right">
        <Icon name="lock" size={11} className="ml-auto text-muted-foreground/40" />
      </td>
    );
  }
  const value = def.value(row);
  if (value == null) {
    return (
      <td className="px-3 py-3 text-right">
        {isLoading ? <Skeleton className="ml-auto h-4 w-10" /> : <span className="font-mono text-xs text-muted-foreground/40">—</span>}
      </td>
    );
  }
  const tier = def.tier(value);
  const vs = standing(def, value, benchmark);
  return (
    <td className="px-3 py-3 text-right align-middle">
      <div className="inline-flex flex-col items-end gap-1">
        <span className={cn("font-mono text-sm font-semibold tabular-nums leading-none", tier === "none" ? "text-foreground" : TIER_TEXT[tier])}>
          {def.format(value)}
        </span>
        {def.bar && (
          <span className="block h-0.5 w-12 overflow-hidden rounded-full bg-border/60">
            <span className={cn("block h-full rounded-full", TIER_BAR[tier === "none" ? "good" : tier])} style={{ width: `${def.bar(value)}%` }} />
          </span>
        )}
        {vs && vs !== "even" && (
          <span className={cn("font-mono text-[10px] leading-none", vs === "ahead" ? "text-emerald-400" : "text-red-400")}>
            {vs === "ahead" ? "▲ above median" : "▼ below median"}
          </span>
        )}
      </div>
    </td>
  );
}

export function Favicon({ domain, name, size = 28 }: { domain: string; name: string; size?: number }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted"
      style={{ height: size, width: size }}
    >
      {failed ? (
        <span className="font-mono text-[11px] font-bold uppercase text-muted-foreground">{name[0] ?? "?"}</span>
      ) : (
        <img
          src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
          alt=""
          className="h-4 w-4 object-contain"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
